import React from 'react';

const ProspectEvaluationSummary = ({ prospectEvaluation, headerActions }) => {
    if (!prospectEvaluation) {
        return null;
    }

    const { passed, details } = prospectEvaluation;

    // Get status visuals
    const getStatusVisuals = () => {
        if (passed) {
            return {
                accent: '#34d399',
                border: 'rgba(52, 211, 153, 0.65)',
                gradient: 'linear-gradient(135deg, rgba(52, 211, 153, 0.24), rgba(52, 211, 153, 0.08))',
                shadow: 'rgba(52, 211, 153, 0.22)',
                label: 'Passed',
                backgroundColor: 'rgba(52, 211, 153, 0.2)',
                color: '#34d399'
            };
        }
        return {
            accent: '#f87171',
            border: 'rgba(248, 113, 113, 0.65)',
            gradient: 'linear-gradient(135deg, rgba(248, 113, 113, 0.22), rgba(248, 113, 113, 0.08))',
            shadow: 'rgba(248, 113, 113, 0.2)',
            label: 'Failed',
            backgroundColor: 'rgba(248, 113, 113, 0.2)',
            color: '#f87171'
        };
    };

    const statusVisuals = getStatusVisuals();

    return (
        <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h4 style={{ color: '#e0e0e0', fontWeight: '600', margin: 0 }}>Prospect Evaluation</h4>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            color: statusVisuals.color,
                            backgroundColor: statusVisuals.backgroundColor,
                            border: `1px solid ${statusVisuals.border}`
                        }}
                    >
                        {statusVisuals.label}
                    </span>
                </div>
                {headerActions && (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {headerActions}
                    </div>
                )}
            </div>
            <div style={{ 
                backgroundColor: '#2d2d2d',
                border: '1px solid #404040',
                borderRadius: '6px',
                padding: '15px'
            }}>
                <div
                    style={{
                        marginBottom: '16px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '14px'
                    }}
                >
                    {/* Status Card */}
                    <div
                        style={{
                            background: statusVisuals.gradient,
                            border: `1px solid ${statusVisuals.border}`,
                            borderRadius: '16px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxShadow: `0 12px 28px ${statusVisuals.shadow}`
                        }}
                    >
                        <span
                            style={{
                                color: '#9ca3af',
                                fontSize: '12px',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                fontWeight: 600
                            }}
                        >
                            Status
                        </span>
                        <span
                            style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: '#f9fafb'
                            }}
                        >
                            {statusVisuals.label}
                        </span>
                        <span
                            style={{
                                color: statusVisuals.accent,
                                fontSize: '13px',
                                fontWeight: 600
                            }}
                        >
                            {passed ? 'All checks passed' : 'Some checks failed'}
                        </span>
                    </div>

                    {/* Good Contacts Card */}
                    {details?.good_contacts_count !== undefined && (
                        <div
                            style={{
                                background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.18), rgba(96, 165, 250, 0.06))',
                                border: '1px solid rgba(96, 165, 250, 0.45)',
                                borderRadius: '16px',
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxShadow: '0 12px 28px rgba(37, 99, 235, 0.18)'
                            }}
                        >
                            <span
                                style={{
                                    color: '#9ca3af',
                                    fontSize: '12px',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    fontWeight: 600
                                }}
                            >
                                Good Contacts
                            </span>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '6px'
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '28px',
                                        fontWeight: 700,
                                        color: '#34d399'
                                    }}
                                >
                                    {details.good_contacts_count}
                                </span>
                                {details.total_contacts !== undefined && (
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            color: '#9ca3af',
                                            fontWeight: 500
                                        }}
                                    >
                                        / {details.total_contacts}
                                    </span>
                                )}
                            </div>
                            <span
                                style={{
                                    color: '#60a5fa',
                                    fontSize: '13px',
                                    fontWeight: 600
                                }}
                            >
                                {details.good_contacts_count > 0 ? 'Suitable contacts found' : 'No suitable contacts'}
                            </span>
                        </div>
                    )}

                    {/* Pages Crawled Card */}
                    {details?.total_pages_crawled !== undefined && (
                        <div
                            style={{
                                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.18), rgba(251, 191, 36, 0.06))',
                                border: '1px solid rgba(251, 191, 36, 0.45)',
                                borderRadius: '16px',
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxShadow: '0 12px 28px rgba(251, 191, 36, 0.18)'
                            }}
                        >
                            <span
                                style={{
                                    color: '#9ca3af',
                                    fontSize: '12px',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    fontWeight: 600
                                }}
                            >
                                Pages Crawled
                            </span>
                            <span
                                style={{
                                    fontSize: '28px',
                                    fontWeight: 700,
                                    color: '#f9fafb'
                                }}
                            >
                                {details.total_pages_crawled}
                            </span>
                            <span
                                style={{
                                    color: '#fbbf24',
                                    fontSize: '13px',
                                    fontWeight: 600
                                }}
                            >
                                {details.max_pages_reached ? 'Max limit reached' : 'Crawl successful'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProspectEvaluationSummary;

